import type { MenuItem } from "@v1/components/designSystem/menu/Menu";
import type { ReploComponent } from "schemas/component";
import type { BundleDiscountThreshold } from "schemas/generated/bundleDiscounts";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import DeprecatedLabel from "@v1/components/designSystem/label/Label";
import Selectable from "@v1/components/designSystem/Selectable";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import { Banknote, Percent, Plus, X } from "lucide-react";

const DEFAULT_BUNDLE_DISCOUNT_THRESHOLDS = [
  { quantity: 3, discount: { type: "percentageOff", value: 25 } },
];

const IntegerInputSelector = ({
  value,
  min,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  menuItems?: MenuItem[];
}) => {
  return (
    <DeprecatedInput
      min={min}
      type="number"
      value={value}
      onChange={(e) => {
        onChange(Number(e.target.value));
      }}
    />
  );
};

const BasicSettings = ({ component }: { component: ReploComponent }) => {
  const applyComponentAction = useApplyComponentAction();
  const isTiered = Boolean(component.props.isTiered);
  const bundleDiscountThresholds =
    component.props.bundleDiscountThresholds ??
    (DEFAULT_BUNDLE_DISCOUNT_THRESHOLDS as BundleDiscountThreshold[]);
  const handleUpdateDiscountThreshold = (
    thresholdIndex: number,
    updatedThreshold: BundleDiscountThreshold,
  ) => {
    const updatedBundleDiscountThresholds = [
      ...bundleDiscountThresholds.slice(0, thresholdIndex),
      updatedThreshold,
      ...bundleDiscountThresholds.slice(thresholdIndex + 1),
    ];
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        bundleDiscountThresholds: updatedBundleDiscountThresholds,
      },
    });
  };
  const handleAddDiscountThreshold = () => {
    const lastThreshold = bundleDiscountThresholds.slice(-1)[0]!;
    const newThreshold = {
      quantity: lastThreshold.quantity + 1,
      discount: lastThreshold.discount,
    };
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        bundleDiscountThresholds: [...bundleDiscountThresholds, newThreshold],
      },
    });
  };
  const handleRemoveDiscountThreshold = (index: number) => {
    const updatedBundleDiscountThresholds = [
      ...bundleDiscountThresholds.slice(0, index),
      ...bundleDiscountThresholds.slice(index + 1),
    ];
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        bundleDiscountThresholds: updatedBundleDiscountThresholds,
      },
    });
  };
  const toggleIsTiered = () => {
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        isTiered: !isTiered,
      },
    });
  };
  const showRemoveButton = bundleDiscountThresholds.length > 1;
  const gridClassName = (() => {
    const numColumns = 3 + (isTiered ? 1 : 0) + (showRemoveButton ? 1 : 0);
    if (numColumns === 4) {
      if (isTiered) {
        return "grid-cols-[12px_1fr_1fr_1fr]";
      } else {
        return "grid-cols-[1fr_1fr_1fr_24px]";
      }
    } else if (numColumns === 5) {
      return "grid-cols-[12px_1fr_1fr_1fr_24px]";
    } else {
      return "grid-cols-[1fr_1fr_1fr]";
    }
  })();
  return (
    <DeprecatedLabeledControl
      size="sm"
      label="Configure Bundle"
      description="Set bundle sizes and rewards"
      descriptionPosition="top"
    >
      <div className="flex flex-col gap-1.5">
        <div className={twMerge("grid gap-0.5 w-full", gridClassName)}>
          {isTiered && <div />}
          <DeprecatedLabel size="sm" label="Size" />
          <DeprecatedLabel size="sm" label="Discount" />
          <div />
          {showRemoveButton && <div />}
          {bundleDiscountThresholds.map(({ quantity, discount }, index) => {
            const lastQuantity =
              bundleDiscountThresholds[index - 1]?.quantity ?? 0;
            const tieredSymbol =
              index === bundleDiscountThresholds.length - 1 ? "=" : "≥";
            return (
              <React.Fragment key={quantity}>
                {isTiered && (
                  <span className="text-xs text-placeholder flex flex-col items-center justify-center">
                    {tieredSymbol}
                  </span>
                )}
                <div>
                  <IntegerInputSelector
                    min={lastQuantity + 1}
                    value={quantity}
                    onChange={(newQuantity) => {
                      return handleUpdateDiscountThreshold(index, {
                        discount,
                        quantity: Number(newQuantity),
                      });
                    }}
                  />
                </div>
                <div>
                  <IntegerInputSelector
                    value={discount.value}
                    onChange={(newDiscountValue) => {
                      return handleUpdateDiscountThreshold(index, {
                        quantity,
                        discount: {
                          ...discount,
                          value: Number(newDiscountValue),
                        },
                      });
                    }}
                  />
                </div>
                <div>
                  <Selectable
                    layoutClassName="w-full"
                    triggerStartEnhancer={
                      discount.type === "percentageOff" ? (
                        <Percent size={9} />
                      ) : (
                        <Banknote size={9} />
                      )
                    }
                    value={discount.type}
                    onSelect={(
                      newDiscountType: "percentageOff" | "fixedAmount",
                    ) => {
                      handleUpdateDiscountThreshold(index, {
                        quantity,
                        discount: { ...discount, type: newDiscountType },
                      });
                    }}
                    options={[
                      {
                        label: (
                          <div className="flex cursor-pointer items-center gap-1.5">
                            <Percent size={9} /> Percentage
                          </div>
                        ),
                        displayValue: "Percentage",
                        value: "percentageOff",
                      },
                      {
                        label: (
                          <div className="flex cursor-pointer items-center gap-1.5">
                            <Banknote size={9} /> Monetary
                          </div>
                        ),
                        displayValue: "Monetary",
                        value: "fixedAmount",
                      },
                    ]}
                  />
                </div>
                {showRemoveButton && (
                  <DeprecatedIconButton
                    icon={<X size={12} />}
                    onClick={() => handleRemoveDiscountThreshold(index)}
                    variant="tertiary"
                    aria-label="Delete threshold"
                    size="sm"
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div>
          <DeprecatedButton
            size="sm"
            variant="link"
            onClick={() => handleAddDiscountThreshold()}
            aria-label="Add new text style"
            startEnhancer={<Plus size={14} />}
          >
            Add option
          </DeprecatedButton>
        </div>
        <SwitchWithDescription
          isOn={isTiered}
          onChange={() => toggleIsTiered()}
          size="sm"
          label="Tiered rewards"
          tooltipText="If enabled, customers can choose to add more items to the bundle, and discounts will apply for the highest-quantity threshold which has been met."
        />
      </div>
    </DeprecatedLabeledControl>
  );
};

const AdvancedSettings = ({ component }: { component: ReploComponent }) => {
  const applyComponentAction = useApplyComponentAction();
  const { allowMultipleOfSameItem } = component.props;
  const toggleProp = (propName: "allowMultipleOfSameItem") => {
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        [propName]: !component.props[propName],
      },
    });
  };
  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      <SwitchWithDescription
        isOn={Boolean(allowMultipleOfSameItem)}
        onChange={() => toggleProp("allowMultipleOfSameItem")}
        size="sm"
        variant="card"
        label="Allow multiple of same item"
        description="Users can add more than one of each product/variant"
      />
    </div>
  );
};

const BundleDiscountsCustomPropModifier = ({
  component,
}: {
  component: ReploComponent;
}) => {
  const [configView, setConfigView] = React.useState<"basic" | "advanced">(
    "basic",
  );

  return (
    <div className="flex flex-col gap-1.5">
      <DeprecatedToggleGroup
        size="sm"
        options={[
          { label: "Basic", value: "basic" },
          { label: "Advanced", value: "advanced" },
        ]}
        selectedValue={configView}
        onChange={(value) => {
          setConfigView(value as "basic" | "advanced");
        }}
      />
      {configView === "basic" ? (
        <BasicSettings component={component} />
      ) : (
        <AdvancedSettings component={component} />
      )}
    </div>
  );
};

export default BundleDiscountsCustomPropModifier;
